import {
  CURRENT_APP_BUILD_ID,
  CURRENT_APP_BUILD_TIME,
  CURRENT_APP_VERSION,
} from './appVersion.generated'

export type AppVersionPayload = {
  buildId?: string
  version?: string
  buildTime?: string
  gitSha?: string
}

export type AppVersionUpdateDetail = {
  payload?: AppVersionPayload
  versionId: string
}

const APP_VERSION_UPDATE_EVENT = 'megapay:app-version-update'
const PENDING_VERSION_SESSION_KEY = 'megapay:pending-app-version'
const RELOAD_THROTTLE_MS = 15000
const RELOAD_SESSION_KEY = 'megapay:last-version-reload-at'

const getVersionUrl = () => {
  const publicUrl = process.env.PUBLIC_URL || ''
  return `${publicUrl}/version.json?ts=${Date.now()}`
}

const getVersionId = (payload?: AppVersionPayload) =>
  payload?.buildId ||
  [payload?.version, payload?.gitSha, payload?.buildTime].filter(Boolean).join('-')

const fetchServerAppVersion = async () => {
  const response = await fetch(getVersionUrl(), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
  if (!response.ok) {
    return undefined
  }

  return (await response.json()) as AppVersionPayload
}

const reloadWithVersion = (nextVersionId: string, force = false) => {
  const now = Date.now()
  const lastReloadAt = Number(sessionStorage.getItem(RELOAD_SESSION_KEY) || 0)
  if (!force && now - lastReloadAt < RELOAD_THROTTLE_MS) {
    return
  }

  sessionStorage.setItem(RELOAD_SESSION_KEY, now.toString())
  const url = new URL(window.location.href)
  url.searchParams.set('_appVersion', nextVersionId.slice(0, 48))
  window.location.replace(url.toString())
}

const isNewAppVersionAvailable = (payload?: AppVersionPayload) => {
  const nextVersionId = payload ? getVersionId(payload) : ''
  return !!nextVersionId && nextVersionId !== CURRENT_APP_BUILD_ID
}

const getCurrentUrlVersionId = () => {
  try {
    return new URL(window.location.href).searchParams.get('_appVersion') || ''
  } catch {
    return ''
  }
}

const shouldAutoReloadForInitialVersion = (nextVersionId: string) =>
  !!nextVersionId && getCurrentUrlVersionId() !== nextVersionId.slice(0, 48)

const readPendingAppVersion = (): AppVersionPayload | undefined => {
  try {
    const rawValue = sessionStorage.getItem(PENDING_VERSION_SESSION_KEY)
    return rawValue ? (JSON.parse(rawValue) as AppVersionPayload) : undefined
  } catch {
    return undefined
  }
}

const clearPendingAppVersion = () => {
  try {
    sessionStorage.removeItem(PENDING_VERSION_SESSION_KEY)
  } catch {}
}

const notifyAppVersionAvailable = (payload?: AppVersionPayload) => {
  const versionId = getVersionId(payload)
  if (!versionId) {
    return
  }

  try {
    sessionStorage.setItem(PENDING_VERSION_SESSION_KEY, JSON.stringify(payload))
  } catch {}

  window.dispatchEvent(
    new CustomEvent<AppVersionUpdateDetail>(APP_VERSION_UPDATE_EVENT, {
      detail: {payload, versionId},
    })
  )
}

export {
  APP_VERSION_UPDATE_EVENT,
  CURRENT_APP_BUILD_ID,
  CURRENT_APP_BUILD_TIME,
  CURRENT_APP_VERSION,
  clearPendingAppVersion,
  fetchServerAppVersion,
  getVersionId,
  isNewAppVersionAvailable,
  notifyAppVersionAvailable,
  readPendingAppVersion,
  reloadWithVersion,
  shouldAutoReloadForInitialVersion,
}
